import { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  LayoutDashboard,
  Users,
  TrendingUp,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Activity,
  ChevronDown,
  Copy,
  UserPlus,
  CreditCard,
  BarChart3,
  Wifi,
  WifiOff,
  RefreshCw,
  Plug,
  CheckCircle,
  Loader2,
  Wallet,
  PieChart
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Clients', href: '/clients', icon: Users },
  { name: 'Positions', href: '/positions', icon: TrendingUp },
  { name: 'Copy Trading', href: '/copy-trading', icon: Copy },
  { name: 'IB Portal', href: '/ib-portal', icon: UserPlus },
  { name: 'Payments', href: '/payments', icon: CreditCard },
  { name: 'Audit Logs', href: '/audit-logs', icon: FileText },
  { name: 'Wealth Management', href: '/wealth-management', icon: Wallet, adminOnly: true },
  { name: 'Book Profitability', href: '/book-profitability', icon: PieChart, adminOnly: true },
  { name: 'CEO Reports', href: '/ceo-reports', icon: BarChart3, adminOnly: true },
  { name: 'Settings', href: '/settings', icon: Settings },
];

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mt5Status, setMt5Status] = useState({ connected: false, loading: false, lastChecked: null });

  // Check MT5 connection status
  const checkMT5Status = useCallback(async (showToast = false) => {
    try {
      setMt5Status(prev => ({ ...prev, loading: true }));
      const response = await api.mt5.getStatus();
      const isConnected = response.data?.connected || false;
      setMt5Status({
        connected: isConnected,
        loading: false,
        lastChecked: new Date().toLocaleTimeString()
      });
      if (showToast) {
        if (isConnected) {
          toast.success('MT5 Connected', { description: 'Live data feed active' });
        } else {
          toast.warning('MT5 Disconnected', { description: 'Using mock data' });
        }
      }
    } catch (error) {
      setMt5Status({ connected: false, loading: false, lastChecked: new Date().toLocaleTimeString() });
      if (showToast) {
        toast.error('MT5 Status Check Failed', { description: error.message });
      }
    }
  }, []);

  // Connect to MT5
  const connectMT5 = async () => {
    try {
      setMt5Status(prev => ({ ...prev, loading: true }));
      toast.loading('Connecting to MT5...', { id: 'mt5-connect' });
      const response = await api.mt5.testConnection();
      const isConnected = response.data?.status === 'connected';
      if (isConnected) {
        setMt5Status({ connected: true, loading: false, lastChecked: new Date().toLocaleTimeString() });
        toast.success('MT5 Connected Successfully', { 
          id: 'mt5-connect',
          description: response.data?.message || 'Live data feed active'
        });
      } else {
        throw new Error(response.data?.error || response.data?.message || 'Connection failed');
      }
    } catch (error) {
      setMt5Status(prev => ({ ...prev, connected: false, loading: false }));
      toast.error('MT5 Connection Failed', { 
        id: 'mt5-connect',
        description: error.response?.data?.detail || error.message 
      });
    }
  };

  // Test MT5 connectivity
  const testMT5 = async () => {
    try {
      setMt5Status(prev => ({ ...prev, loading: true }));
      toast.loading('Testing MT5 connection...', { id: 'mt5-test' });
      const response = await api.mt5.testConnection();
      const isConnected = response.data?.status === 'connected';
      setMt5Status(prev => ({ 
        ...prev, 
        connected: isConnected, 
        loading: false,
        lastChecked: new Date().toLocaleTimeString()
      }));
      if (isConnected) {
        const userCount = response.data?.available_users?.length || 0;
        toast.success('MT5 Test Passed', { 
          id: 'mt5-test',
          description: `Server: ${response.data?.server || 'N/A'} | Users: ${userCount}`
        });
      } else {
        toast.warning('MT5 Test Failed', { 
          id: 'mt5-test',
          description: response.data?.error || response.data?.message || 'Connection issue'
        });
      }
    } catch (error) {
      setMt5Status(prev => ({ ...prev, loading: false }));
      toast.error('MT5 Test Error', { 
        id: 'mt5-test',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  // Check status on mount
  useEffect(() => {
    checkMT5Status(false);
    // Re-check every 30 seconds
    const interval = setInterval(() => checkMT5Status(false), 30000);
    return () => clearInterval(interval);
  }, [checkMT5Status]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  return (
    <div className="min-h-screen bg-background noise-bg">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-card border-r border-border
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-6 border-b border-border">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_forexcrm-sync/artifacts/lepttxy2_image%20%281%29.png" 
                alt="TD-Capital" 
                className="w-10 h-10 object-contain"
              />
              <span className="font-bold text-lg tracking-tight">TD-Capital</span>
            </div>
            <button 
              className="lg:hidden p-1 hover:bg-muted rounded"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              // Hide admin-only items from non-admin users
              if (item.adminOnly && user?.role !== 'admin') {
                return null;
              }
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `
                    sidebar-item ${isActive ? 'active' : ''}
                  `}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.name}</span>
                </NavLink>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 px-2">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-primary/20 text-primary text-sm">
                  {getInitials(user?.full_name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground truncate capitalize">{user?.role}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="h-16 border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-30">
          <div className="h-full px-4 lg:px-8 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                className="lg:hidden p-2 hover:bg-muted rounded-lg"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="hidden sm:flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Tenant:</span>
                <span className="text-sm font-mono bg-muted px-2 py-1 rounded">{user?.tenant_id}</span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* MT5 Status indicator with controls */}
              <div className="hidden sm:flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                        mt5Status.connected 
                          ? 'bg-emerald-500/10 hover:bg-emerald-500/20' 
                          : 'bg-amber-500/10 hover:bg-amber-500/20'
                      }`}
                      data-testid="mt5-status-btn"
                    >
                      {mt5Status.loading ? (
                        <Loader2 className="w-3 h-3 animate-spin text-cyan-400" />
                      ) : mt5Status.connected ? (
                        <Wifi className="w-3 h-3 text-emerald-400" />
                      ) : (
                        <WifiOff className="w-3 h-3 text-amber-400" />
                      )}
                      <span className={`text-xs font-medium ${
                        mt5Status.connected ? 'text-emerald-400' : 'text-amber-400'
                      }`}>
                        {mt5Status.loading ? 'Checking...' : mt5Status.connected ? 'MT5 Live' : 'MT5 Mock'}
                      </span>
                      <ChevronDown className="w-3 h-3 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-2">
                      <div className="flex items-center gap-2 mb-1">
                        {mt5Status.connected ? (
                          <CheckCircle className="w-4 h-4 text-emerald-400" />
                        ) : (
                          <WifiOff className="w-4 h-4 text-amber-400" />
                        )}
                        <span className="text-sm font-medium">
                          {mt5Status.connected ? 'Connected to MT5' : 'Disconnected'}
                        </span>
                      </div>
                      {mt5Status.lastChecked && (
                        <p className="text-xs text-muted-foreground">
                          Last checked: {mt5Status.lastChecked}
                        </p>
                      )}
                    </div>
                    <DropdownMenuSeparator />
                    {!mt5Status.connected && (
                      <DropdownMenuItem 
                        onClick={connectMT5}
                        disabled={mt5Status.loading}
                        data-testid="mt5-connect-btn"
                      >
                        <Plug className="w-4 h-4 mr-2" />
                        Connect to MT5
                      </DropdownMenuItem>
                    )}
                    {mt5Status.connected && (
                      <DropdownMenuItem 
                        onClick={() => checkMT5Status(true)}
                        disabled={mt5Status.loading}
                        data-testid="mt5-refresh-btn"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh Status
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem 
                      onClick={testMT5}
                      disabled={mt5Status.loading}
                      data-testid="mt5-test-btn"
                    >
                      <Activity className="w-4 h-4 mr-2" />
                      Test Connection
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* User dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary/20 text-primary text-xs">
                        {getInitials(user?.full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.full_name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8 relative z-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
